import { 
  Building2, 
  Shield, 
  Users, 
  Settings, 
  LineChart, 
  Network,
  Lock,
  Clock,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { animations } from '@/lib/animations';
import { ROUTES, CALENDLY_URL } from '@/lib/constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/lib/translations';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Enterprise = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language].enterprise;

  const features: Feature[] = [
    {
      icon: <Shield className="w-12 h-12 text-primary" />,
      title: t.features.items.security.title,
      description: t.features.items.security.description
    },
    {
      icon: <Users className="w-12 h-12 text-primary" />,
      title: t.features.items.teams.title,
      description: t.features.items.teams.description
    },
    {
      icon: <Settings className="w-12 h-12 text-primary" />,
      title: t.features.items.customization.title,
      description: t.features.items.customization.description
    },
    {
      icon: <LineChart className="w-12 h-12 text-primary" />,
      title: t.features.items.analytics.title,
      description: t.features.items.analytics.description
    },
    {
      icon: <Network className="w-12 h-12 text-primary" />,
      title: t.features.items.support.title,
      description: t.features.items.support.description
    },
    {
      icon: <Clock className="w-12 h-12 text-primary" />,
      title: t.features.items.training.title,
      description: t.features.items.training.description
    }
  ];

  const benefits = t.benefits.items;

  return (
    <div className={`min-h-screen bg-background ${animations.smoothScroll}`}>
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className={`text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/50 bg-clip-text text-transparent ${animations.fadeInSlideUp}`}>
            {t.hero.title}
          </h1>
          <p className={`text-xl text-muted-foreground mb-8 ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.hero.subtitle}
          </p>
          <div className={`flex justify-center gap-4 ${animations.fadeInSlideUp} ${animations.delay300}`}>
            <Button 
              size="lg" 
              className={`group ${animations.buttonHover}`}
              onClick={() => navigate(ROUTES.CONTACT_SALES)}
            >
              {t.hero.contactSales}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              className={animations.buttonHover}
              onClick={() => window.open(CALENDLY_URL, '_blank')}
            >
              {t.hero.watchDemo}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-16">
        <h2 className={`text-3xl font-bold text-center mb-12 ${animations.fadeInSlideUp}`}>
          {t.features.title}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card 
              key={index}
              className={`p-6 ${animations.cardHover} ${animations.fadeInSlideUp}`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className={`mb-4 ${animations.float}`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-16 bg-muted/50">
        <div className="max-w-4xl mx-auto">
          <h2 className={`text-3xl font-bold text-center mb-12 ${animations.fadeInSlideUp}`}>
            {t.benefits.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className={`flex items-center gap-2 ${animations.fadeInSlideUp}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CheckCircle2 className="w-5 h-5 text-primary" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`mb-6 ${animations.float}`}>
            <Lock className="w-16 h-16 text-primary mx-auto" />
          </div>
          <h2 className={`text-3xl font-bold mb-4 ${animations.fadeInSlideUp}`}>
            {t.security.title}
          </h2>
          <p className={`text-xl text-muted-foreground mb-8 ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.security.description}
          </p>
          <Button 
            variant="outline"
            className={`${animations.buttonHover} ${animations.fadeInSlideUp} ${animations.delay300}`}
          >
            {t.security.cta}
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-16 bg-muted/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className={`text-3xl font-bold mb-4 ${animations.fadeInSlideUp}`}>
            {t.cta.title}
          </h2>
          <p className={`text-xl text-muted-foreground mb-8 ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.cta.description}
          </p>
          <div className={`flex justify-center gap-4 ${animations.fadeInSlideUp} ${animations.delay300}`}>
            <Button 
              size="lg"
              className={`group ${animations.buttonHover}`}
              onClick={() => window.open(CALENDLY_URL, '_blank')}
            >
              {t.cta.schedule}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className={animations.buttonHover}
            >
              {t.cta.brochure}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enterprise; 